import { formatDateDDMMYY } from '../../utils/dateFormat';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DnsIcon from '@mui/icons-material/Dns';
import HdIcon from '@mui/icons-material/Hd';
import MemoryIcon from '@mui/icons-material/Memory';
import PublicIcon from '@mui/icons-material/Public';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import Inventory2Icon from '@mui/icons-material/Inventory2';

import { useAuth } from '../../hooks';
import { BillingConfig, NextInvoice, getBillingAccountSummary, getBillingConfig, initiatePayment, previewPaymentIntent, scheduleOutputPackageChange } from '../../services/billingService';
import { getMyChannels } from '../../services/channelsService';
import { getTranscoders } from '../../services/streamsService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { ConsolePageShell } from '../console/ConsolePageShell';
import { navigateToTrustedRedirect } from '../../utils/safeRedirect';

type AddonType =
  | 'ADD_CHANNEL'
  | 'PLAYKASTR'
  | 'SOCIAL_STREAM'
  | 'EXTRA_PASSTHROUGH'
  | 'MANAGED_SERVICE'
  | 'MANAGED_SERVICE_UPGRADE'
  | 'FULL_TRANSCODED'
  | 'UPGRADE_INCLUDED'
  | 'HLS_STANDARD'
  | 'HLS_SCTE35'
  | 'PACKAGE_STREAMER_LITE'
  | 'PACKAGE_STREAMER_PRO'
  | 'PACKAGE_BROADCAST_STARTER'
  | 'PACKAGE_BROADCAST_LITE'
  | 'PACKAGE_BROADCAST_PRO'
  | 'PACKAGE_FAST_LITE'
  | 'PACKAGE_FAST_PRO';

type ProductGroup = 'Channel' | 'Packages' | 'Automation' | 'Delivery' | 'Web / OTT';
type AddonBillingMode = 'PRORATED' | 'NEW_SERVICE_DATE';

interface CartItem {
  id: string;
  type: AddonType;
  tierCode?: string;
  label: string;
  description: string;
  price: number;
  priceSetup: number;
  quantity: number;
  channelId?: number;
  channelName?: string;
  targetTranscoderId?: number;
  targetTranscoderLabel?: string;
  quantityMode?: string;
}

interface ProductConfig {
  type: AddonType;
  tierCode?: string;
  tierOptions?: Array<{ code: string; label: string; price: number; storageLabel: string }>;
  group: ProductGroup;
  title: string;
  desc: string;
  icon: React.ElementType;
  price: number;
  setup?: number;
  features: string[];
  semantics: string;
  requiresChannel: boolean;
  isAvailable?: (channel: any | null) => boolean;
  unavailableMessage?: string;
  quantityMode?: string;
  isPackage?: boolean;
}

const v = (name: string) => `var(${name})`;

const SKY = v('--primary-light');
const SKY_STRONG = v('--bg-card-hover');
const LIGHT = v('--bg-body');
const TEXT = v('--text-main');
const MUTED = v('--text-secondary');
const BORDER = v('--border');

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: BORDER,
  bgcolor: v('--bg-card'),
  color: TEXT,
} as const;

const labelSx = {
  fontSize: '0.68rem',
  fontWeight: 900,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: MUTED,
} as const;

const primaryButtonSx = {
  borderRadius: 0,
  textTransform: 'none' as const,
  fontWeight: 900,
  minHeight: 38,
} as const;

const money = (currency: string, value: number) => `${currency} ${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: Number.isInteger(Number(value || 0)) ? 0 : 2,
  maximumFractionDigits: 2,
})}`;

const getChannelDisplayId = (channel: any) => channel?.channelDisplayId || channel?.displayId || (channel?.channelHashId ? `CH-${channel.channelHashId}` : channel?.hashId ? `CH-${channel.hashId}` : 'CH-PENDING');
const getTranscoderDisplayId = (output: any) => output?.transcoderDisplayId || output?.displayId || (output?.transcoderHashId ? `TR-${output.transcoderHashId}` : output?.hashId ? `TR-${output.hashId}` : 'Output');

const getProductSemantics = (type: AddonType) => {
  if (String(type).startsWith('PACKAGE_')) return 'OUTPUT_PACKAGE';
  if (type === 'MANAGED_SERVICE') return 'MANAGED_SERVICE';
  if (type === 'MANAGED_SERVICE_UPGRADE') return 'MANAGED_SERVICE_UPGRADE';
  if (type === 'FULL_TRANSCODED' || type === 'HLS_STANDARD' || type === 'HLS_SCTE35') return 'CPU_ONLY';
  if (type === 'UPGRADE_INCLUDED') return 'UPGRADE_EXISTING';
  if (type === 'SOCIAL_STREAM') return 'SOCIAL_RTMP_BUNDLE';
  return 'OUTPUT';
};

const cartStorageKeyFor = (userId?: number | string, email?: string) => `streamy.addonStore.cart.${userId || email || 'default'}`;

const groupMeta: Record<ProductGroup, { title: string; subtitle: string; icon: React.ElementType }> = {
  Channel: {
    title: 'Channel',
    subtitle: 'Create a new broadcast workspace before adding delivery services.',
    icon: DnsIcon,
  },
  Packages: {
    title: 'Output Packages',
    subtitle: 'One discounted package per channel. Individual outputs can be added separately at any time.',
    icon: Inventory2Icon,
  },
  Automation: {
    title: 'Automation',
    subtitle: 'Playlist, scheduling, EPG, and playout automation for eligible channels.',
    icon: AutoAwesomeIcon,
  },
  Delivery: {
    title: 'Delivery',
    subtitle: 'Direct pass, managed service, social streaming, and remapped output capacity.',
    icon: BoltIcon,
  },
  'Web / OTT': {
    title: 'Web / OTT',
    subtitle: 'HTTP playback outputs for web players, apps, and OTT distribution.',
    icon: HdIcon,
  },
};

const ProductCard = ({
  product,
  currency,
  selectedChannelId,
  selectedTranscoderId,
  onChannelChange,
  onTranscoderChange,
  selectedPlaykastrTierCode,
  onPlaykastrTierChange,
  channels,
  transcoders,
  onAdd,
}: {
  product: ProductConfig;
  currency: string;
  selectedChannelId: string;
  selectedTranscoderId: string;
  onChannelChange: (value: string) => void;
  onTranscoderChange: (value: string) => void;
  selectedPlaykastrTierCode: string;
  onPlaykastrTierChange: (value: string) => void;
  channels: any[];
  transcoders: any[];
  onAdd: () => void;
}) => {
  const Icon = product.icon;
  const needsTranscoder = product.type === 'UPGRADE_INCLUDED' || product.type === 'MANAGED_SERVICE_UPGRADE';
  const availabilityAllowed = product.isAvailable ? product.isAvailable(channels.find((c) => String(c.id) === selectedChannelId) || null) : true;
  const hasRequiredTarget = !product.requiresChannel || (Boolean(selectedChannelId) && (!needsTranscoder || Boolean(selectedTranscoderId)));
  const canAdd = hasRequiredTarget && availabilityAllowed;

  return (
    <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.25, sm: 1.5 }, minHeight: 280, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ width: 40, height: 40, border: '1px solid', borderColor: v('--border-strong'), bgcolor: SKY_STRONG, color: v('--secondary'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={900} sx={{ color: TEXT, fontSize: '0.98rem' }}>{product.title}</Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.76rem', mt: 0.25 }}>{product.desc}</Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography fontWeight={950} sx={{ color: v('--secondary'), fontSize: '1.35rem' }}>{money(currency, product.price)}</Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>/mo</Typography>
        {product.setup ? <Chip size="small" label={`+ ${money(currency, product.setup)} setup`} sx={{ borderRadius: 0, bgcolor: SKY, color: TEXT, fontWeight: 800 }} /> : null}
      </Stack>

      <Stack spacing={0.55} sx={{ flex: 1 }}>
        {product.features.map((feature) => (
          <Stack key={feature} direction="row" spacing={0.75} alignItems="flex-start">
            <CheckCircleIcon sx={{ fontSize: 15, color: v('--success'), mt: '2px' }} />
            <Typography sx={{ color: MUTED, fontSize: '0.72rem' }}>{feature}</Typography>
          </Stack>
        ))}
      </Stack>

      {product.requiresChannel && (
        <FormControl size="small" fullWidth>
          <InputLabel>Assign to channel</InputLabel>
          <Select value={selectedChannelId} label="Assign to channel" onChange={(event) => onChannelChange(String(event.target.value))}>
            {channels.map((channel) => (
              <MenuItem key={channel.id} value={String(channel.id)}>
                {channel.name} · {getChannelDisplayId(channel)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {product.type === 'PLAYKASTR' && product.tierOptions && (
        <FormControl size="small" fullWidth>
          <InputLabel>Storage plan</InputLabel>
          <Select value={selectedPlaykastrTierCode} label="Storage plan" onChange={(event) => onPlaykastrTierChange(String(event.target.value))}>
            {product.tierOptions.map((tier) => (
              <MenuItem key={tier.code} value={tier.code}>
                {tier.label} · {money(currency, tier.price)}/mo · {tier.storageLabel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {needsTranscoder && (
        <FormControl size="small" fullWidth disabled={!selectedChannelId}>
          <InputLabel>Output to upgrade</InputLabel>
          <Select value={selectedTranscoderId} label="Output to upgrade" onChange={(event) => onTranscoderChange(String(event.target.value))}>
            {transcoders.length === 0 && <MenuItem disabled>No upgradeable direct outputs found</MenuItem>}
            {transcoders.map((output) => (
              <MenuItem key={output.id} value={String(output.id)} disabled={output.videoCodec !== 'copy' || output.isSocialStream}>
                {output.platformDistributor || output.regionLocation || getTranscoderDisplayId(output)} {output.videoCodec !== 'copy' ? '· already managed' : output.isSocialStream ? '· social stream' : '· passthrough'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button variant="contained" startIcon={<AddIcon />} disabled={!canAdd} onClick={onAdd} sx={primaryButtonSx}>
        {product.isPackage ? (channels.find((c) => String(c.id) === selectedChannelId)?.outputPackageCode ? `Change to ${product.title}` : `Choose ${product.title}`) : product.requiresChannel ? `Add ${product.title}` : 'Add new channel'}
      </Button>
      {!availabilityAllowed && product.unavailableMessage ? (
        <Typography sx={{ color: MUTED, fontSize: '0.68rem' }}>{product.unavailableMessage}</Typography>
      ) : null}
    </Paper>
  );
};

export const AddonStore: React.FC = () => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const loadedRef = useRef(false);
  const cartHydratedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<BillingConfig | null>(null);
  const [channels, setChannels] = useState<any[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [selectedTranscoderId, setSelectedTranscoderId] = useState('');
  const [selectedPlaykastrTierCode, setSelectedPlaykastrTierCode] = useState('STARTER_CUT_50');
  const [channelTranscoders, setChannelTranscoders] = useState<any[]>([]);
  const gateway = 'manual';
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [effectiveBillingDate, setEffectiveBillingDate] = useState<string | null>(null);
  const [channelBillingPeriods, setChannelBillingPeriods] = useState<Record<string, { start?: string | null; end?: string | null }>>({});
  const [addonBillingMode, setAddonBillingMode] = useState<AddonBillingMode>('PRORATED');
  const [intentPreview, setIntentPreview] = useState<NextInvoice | null>(null);
  const [intentPreviewLoading, setIntentPreviewLoading] = useState(false);
  const cartStorageKey = useMemo(() => cartStorageKeyFor(user?.id, user?.email), [user?.email, user?.id]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    Promise.all([getBillingConfig(), getMyChannels().catch(() => []), getBillingAccountSummary().catch(() => null)])
      .then(([config, chs, summary]) => {
        setPlans(config);
        setChannels(chs);
        setEffectiveBillingDate(summary?.account?.effectiveBillingDate || null);
        setChannelBillingPeriods(Object.fromEntries((summary?.services?.channels || []).map((channel) => [
          String(channel.id),
          { start: channel.currentPeriodStart || null, end: channel.dueDate || null },
        ])));
        if (chs?.[0]?.id) setSelectedChannelId(String(chs[0].id));
      })
      .catch(() => showSnackbar('Store failed to load.', 'error'))
      .finally(() => setLoading(false));
  }, [showSnackbar]);

  useEffect(() => {
    if (!user) return;
    try {
      const stored = localStorage.getItem(cartStorageKey);
      if (!stored) {
        cartHydratedRef.current = true;
        return;
      }
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        cartHydratedRef.current = true;
        return;
      }
      setCart(parsed.filter((item) => item && item.id && item.type && item.label));
      showSnackbar(`You still have ${parsed.length} item${parsed.length === 1 ? '' : 's'} in your cart.`, 'info');
    } catch {
      localStorage.removeItem(cartStorageKey);
    } finally {
      cartHydratedRef.current = true;
    }
  }, [cartStorageKey, showSnackbar, user]);

  useEffect(() => {
    if (!user) return;
    if (!cartHydratedRef.current) return;
    try {
      if (cart.length === 0) localStorage.removeItem(cartStorageKey);
      else localStorage.setItem(cartStorageKey, JSON.stringify(cart));
    } catch {
      // Cart persistence is best-effort only.
    }
  }, [cart, cartStorageKey, user]);

  useEffect(() => {
    if (!selectedChannelId) {
      setChannelTranscoders([]);
      setSelectedTranscoderId('');
      return;
    }
    const selectedChannel = channels.find((channel) => String(channel.id) === selectedChannelId);
    getTranscoders(
      selectedChannel || Number(selectedChannelId),
      selectedChannel?.channelHashId || selectedChannel?.hashId
    )
      .then(setChannelTranscoders)
      .catch(() => setChannelTranscoders([]));
    setSelectedTranscoderId('');
  }, [channels, selectedChannelId]);

  const currentPlan = plans ? (plans.BROADCASTER || plans[user?.role || '']) : null;
  const pricing = currentPlan?.pricing || {};
  const currency = currentPlan?.currency || 'USD';
  const catalogProducts = plans?.CATALOG?.products || [];
  const catalogByCode = useMemo(
    () => new Map(catalogProducts.map((product: any) => [String(product.code), product])),
    [catalogProducts]
  );
  const playkastrTiers = useMemo(
    () =>
      catalogProducts.filter((product: any) => product.category === 'PLAYKASTER').map((product: any) => ({
        code: String(product.code).replace(/^PLAYKASTR_/, ''),
        label: String(product.name),
        price: Number(product.monthly || 0),
        setup: Number(product.setup || 0),
        storageGb: Math.round(Number(product.storageBytes || 0) / 1073741824),
        storageLabel: Number(product.storageBytes || 0) >= 1099511627776 ? '1 TB included' : `${Math.round(Number(product.storageBytes || 0) / 1073741824)} GB included`,
      })),
    [catalogProducts]
  );
  const activePlaykastrTier =
    playkastrTiers.find((tier) => tier.code === selectedPlaykastrTierCode) || playkastrTiers[0] || null;

  const products: ProductConfig[] = useMemo(() => {
    const catalogPrice = (code: string, fallbackPrice = 0) => Number((catalogByCode.get(code) as any)?.monthly ?? fallbackPrice);
    const catalogSetup = (code: string, fallbackSetup = 0) => Number((catalogByCode.get(code) as any)?.setup ?? fallbackSetup);
    const packageProducts: ProductConfig[] = catalogProducts
      .filter((product: any) => product.package === true)
      .map((product: any) => {
        const included = product.entitlements || {};
        const features = [
          included.directPass ? `${included.directPass} Direct Pass` : null,
          included.hlsStandard ? `${included.hlsStandard} HLS Standard` : null,
          included.hlsPlus ? `${included.hlsPlus} HLS+` : null,
          included.remappedOutput ? `${included.remappedOutput} Remapped Output` : null,
          included.socialStream ? `${included.socialStream} Social Streams` : null,
        ].filter(Boolean) as string[];
        return {
          type: product.code as AddonType,
          group: 'Packages',
          title: product.name,
          desc: 'Discounted atomic output package. Included services are managed together.',
          icon: Inventory2Icon,
          price: Number(product.monthly || 0),
          setup: Number(product.setup || 0),
          features,
          semantics: 'OUTPUT_PACKAGE',
          requiresChannel: true,
          quantityMode: 'SINGLE',
          isPackage: true,
          isAvailable: (channel: any) => Number(included.hlsPlus || 0) === 0 && String(channel?.outputPackageCode || '') !== String(product.code),
          unavailableMessage: Number(included.hlsPlus || 0) > 0
            ? 'This package includes HLS+, which is currently under development. Coming soon.'
            : 'This package is already active on the selected channel.',
        };
      });
    return [
    {
      type: 'ADD_CHANNEL', group: 'Channel', title: 'New Channel', desc: 'Broadcast workspace and ingest path. Outputs are selected separately.', icon: DnsIcon,
      price: catalogPrice('CHANNEL', pricing.channelMonthly || 0), setup: catalogSetup('CHANNEL', pricing.channelSetup || 0), requiresChannel: false, semantics: 'CHANNEL',
      features: ['1 input stream', 'No default outputs', 'Choose a package or individual outputs'], quantityMode: 'MULTIPLE',
    },
    ...packageProducts,
    {
      type: 'PLAYKASTR',
      tierCode: activePlaykastrTier?.code || 'STARTER_CUT_50',
      tierOptions: playkastrTiers,
      group: 'Automation',
      title: 'Playkaster',
      desc: 'Playkaster currently under dev. Coming soon.',
      icon: AutoAwesomeIcon,
      price: activePlaykastrTier?.price || Number(currentPlan?.addons?.playkastr?.price || 39),
      setup: activePlaykastrTier?.setup || 69,
      requiresChannel: true,
      semantics: 'OUTPUT',
      features: [
        activePlaykastrTier?.storageLabel || '50 GB included',
        'Playlist automation workspace',
        'XMLTV and Gracenote included',
        'Metadata, artwork, and API access',
        'Requires at least one distribution output',
      ],
      isAvailable: () => false,
      unavailableMessage: 'Playkaster currently under dev. Coming soon.',
    },
    {
      type: 'EXTRA_PASSTHROUGH', group: 'Delivery', title: 'Direct Pass', desc: 'Extra passthrough output for backup or signal distribution.', icon: BoltIcon,
      price: catalogPrice('DIRECT_OUTPUT', pricing.extraPassthrough || 0), requiresChannel: true, semantics: 'OUTPUT',
      features: ['Low latency', 'No transcoding', 'Push or pull workflow'],
    },
    {
      type: 'MANAGED_SERVICE', group: 'Delivery', title: 'Managed Service', desc: 'Full remote tech support and endpoint management.', icon: SupportAgentIcon,
      price: catalogPrice('MANAGED_SERVICE', pricing.managedService || Number(currentPlan?.addons?.managedService?.price || 79)), requiresChannel: true, semantics: 'MANAGED_SERVICE',
      features: ['Full remote tech support', 'Endpoint management included', 'Available worldwide'],
    },
    {
      type: 'MANAGED_SERVICE_UPGRADE', group: 'Delivery', title: 'Upgrade to Managed Service', desc: 'Add full remote tech support to an existing Direct Pass.', icon: UpgradeIcon,
      price: pricing.managedServiceUpgrade || Number(currentPlan?.addons?.managedService?.upgradePrice || 16.49), requiresChannel: true, semantics: 'MANAGED_SERVICE_UPGRADE',
      features: ['Uses an existing direct output', 'Adds full remote tech support', 'Difference from Direct Pass pricing'],
    },
    {
      type: 'SOCIAL_STREAM', group: 'Delivery', title: 'Social Stream', desc: 'RTMP social delivery bundle for preset platforms.', icon: PublicIcon,
      price: catalogPrice('SOCIAL_STREAM', pricing.socialStreamInitial || 24), requiresChannel: true, semantics: 'SOCIAL_RTMP_BUNDLE',
      features: ['First purchase includes 4 RTMP links', 'Preset social platforms', `Additional links ${money(currency, catalogPrice('SOCIAL_STREAM_ADDITIONAL', pricing.socialStreamAdditional || 8))} each`],
    },
    {
      type: 'FULL_TRANSCODED', group: 'Delivery', title: 'Remapped Output', desc: 'Standalone transcoding capacity attached to a channel.', icon: MemoryIcon,
      price: catalogPrice('MANAGED_OUTPUT', pricing.fullTranscoded || 0), requiresChannel: true, semantics: 'CPU_ONLY',
      features: ['Codec controls', 'Bitrate/resolution controls', 'Independent output slot'],
    },
    {
      type: 'UPGRADE_INCLUDED', group: 'Delivery', title: 'Upgrade Slot', desc: 'Upgrade an existing direct pass into a remapped output.', icon: UpgradeIcon,
      price: pricing.upgradeToTranscode || 0, requiresChannel: true, semantics: 'UPGRADE_EXISTING',
      features: ['Keeps existing slot', 'Adds transcode capability', 'Select the output to upgrade'],
    },
    {
      type: 'HLS_STANDARD', group: 'Web / OTT', title: 'HLS', desc: 'HTTP playback link for web, apps, and OTT delivery.', icon: HdIcon,
      price: catalogPrice('HLS_STANDARD', pricing.hlsStandard || 55), requiresChannel: true, semantics: 'CPU_ONLY',
      features: ['One active consumer', 'CDN origin compatible', 'Channel-attached service'],
    },
    {
      type: 'HLS_SCTE35', group: 'Web / OTT', title: 'HLS+', desc: 'HLS+ currently under development. Coming soon.', icon: AutoAwesomeIcon,
      price: catalogPrice('HLS_SCTE35', pricing.hlsScte35 || 109), requiresChannel: true, semantics: 'CPU_ONLY',
      features: ['One active consumer', 'SCTE-35 markers', 'Ad insertion ready'],
      isAvailable: () => false,
      unavailableMessage: 'HLS+ currently under development. Coming soon.',
    },
  ];
  }, [activePlaykastrTier, catalogByCode, catalogProducts, currentPlan?.addons?.managedService?.price, currentPlan?.addons?.managedService?.upgradePrice, currentPlan?.addons?.playkastr?.price, playkastrTiers, pricing.channelMonthly, pricing.channelSetup, pricing.extraPassthrough, pricing.fullTranscoded, pricing.hlsScte35, pricing.hlsStandard, pricing.managedService, pricing.managedServiceUpgrade, pricing.socialStreamAdditional, pricing.socialStreamInitial, pricing.upgradeToTranscode]);

  const checkoutPayload = useMemo(() => ({
    action: 'BATCH_ADD_RESOURCE',
    targetPlan: 'BROADCASTER',
    paymentGateway: gateway,
    options: {
      billingMode: addonBillingMode,
      items: cart.map((item) => ({
        resourceType: item.type,
        tierCode: item.tierCode,
        quantity: item.quantity,
        targetChannelId: item.channelId,
        targetTranscoderId: item.targetTranscoderId,
        semantics: getProductSemantics(item.type),
      })),
    },
  }), [addonBillingMode, cart]);

  useEffect(() => {
    if (!cart.length) {
      setIntentPreview(null);
      setIntentPreviewLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIntentPreviewLoading(true);
      try {
        const preview = await previewPaymentIntent(checkoutPayload as any);
        if (!cancelled) setIntentPreview(preview);
      } catch {
        if (!cancelled) setIntentPreview(null);
      } finally {
        if (!cancelled) setIntentPreviewLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [cart.length, checkoutPayload]);

  if (loading || !plans || !user) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}><CircularProgress /></Box>;
  }

  if (!currentPlan?.addons) {
    return <Box p={4} textAlign="center"><Typography>Store unavailable.</Typography></Box>;
  }

  const selectedChannel = selectedChannelId ? channels.find((channel) => String(channel.id) === selectedChannelId) : null;

  const getCartLineRecurring = (item: CartItem) => {
    if (item.type !== 'SOCIAL_STREAM') return item.price * item.quantity;
    const channel = item.channelId ? channels.find((ch) => ch.id === item.channelId) : null;
    const hasPackageInCart = cart.some((candidate) => candidate.channelId === item.channelId && String(candidate.type).startsWith('PACKAGE_'));
    const hasExistingLinks = Number(channel?.socialStreamLimit || 0) > 0 || hasPackageInCart;
    const initial = pricing.socialStreamInitial || 24;
    const additional = pricing.socialStreamAdditional || 8;
    if (hasExistingLinks) return additional * item.quantity;
    return initial + Math.max(0, item.quantity - 1) * additional;
  };

  const addProductToCart = async (product: ProductConfig) => {
    const channelId = product.requiresChannel ? Number(selectedChannelId) : undefined;
    const channelName = product.requiresChannel ? selectedChannel?.name : undefined;
    const targetTranscoderId = product.type === 'UPGRADE_INCLUDED' || product.type === 'MANAGED_SERVICE_UPGRADE' ? Number(selectedTranscoderId) : undefined;
    const targetTranscoder = targetTranscoderId ? channelTranscoders.find((output) => output.id === targetTranscoderId) : null;
    const tierCode = product.type === 'PLAYKASTR' ? selectedPlaykastrTierCode : product.tierCode;
    const id = `${product.type}-${tierCode || 'base'}-${channelId || 'global'}-${targetTranscoderId || 'none'}`;
    const existing = cart.find((item) => item.id === id);

    if (product.isPackage && selectedChannel?.outputPackageCode) {
      const currentProduct = catalogByCode.get(String(selectedChannel.outputPackageCode)) as any;
      if (currentProduct && Number(product.price) < Number(currentProduct.monthly || 0)) {
        try {
          const response = await scheduleOutputPackageChange({ channelId: channelId!, packageCode: product.type });
          setChannels((previous) => previous.map((channel) => channel.id === channelId ? { ...channel, pendingOutputPackageCode: product.type } : channel));
          showSnackbar(response.message || 'Package downgrade scheduled for the billing anniversary.', 'success');
        } catch (error: any) {
          showSnackbar(error.response?.data?.message || 'Package downgrade could not be scheduled.', 'error');
        }
        return;
      }
    }

    if (product.isPackage && cart.some((item) => item.channelId === channelId && String(item.type).startsWith('PACKAGE_') && item.type !== product.type)) {
      showSnackbar('A channel may have only one output package in the cart.', 'warning');
      return;
    }

    if (product.type === 'PLAYKASTR') {
      const hasCurrentOutput = Boolean(
        selectedChannel?.outputPackageCode
        || Number(selectedChannel?.outputLimit || 0) > 0
        || Number(selectedChannel?.socialStreamLimit || 0) > 0
        || Number(selectedChannel?.hlsLimit || 0) > 0
        || Number(selectedChannel?.hlsPlusLimit || 0) > 0
        || Number(selectedChannel?.cpuLimit || 0) > 0
      );
      const hasCartOutput = cart.some((item) => item.channelId === channelId && item.type !== 'PLAYKASTR');
      if (!hasCurrentOutput && !hasCartOutput) {
        showSnackbar('Add an output package or individual output service before PlayKaster.', 'warning');
        return;
      }
    }

    if (existing) {
      if (product.quantityMode === 'SINGLE') {
        showSnackbar(`${product.title} is already in the cart.`, 'info');
        return;
      }
      setCart((prev) => prev.map((item) => item.id === id ? { ...item, quantity: item.quantity + 1 } : item));
      showSnackbar('Cart updated.', 'success');
      return;
    }

    setCart((prev) => [...prev, {
      id,
      type: product.type,
      tierCode,
      label: product.requiresChannel ? `${product.title} -> ${channelName}` : product.title,
      description: product.desc,
      price: product.price,
      priceSetup: product.setup || 0,
      quantity: 1,
      channelId,
      channelName,
      targetTranscoderId,
      targetTranscoderLabel: targetTranscoder ? (targetTranscoder.platformDistributor || targetTranscoder.regionLocation || getTranscoderDisplayId(targetTranscoder)) : undefined,
      quantityMode: product.quantityMode,
    }]);
    showSnackbar('Added to cart.', 'success');
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      if (item.quantityMode === 'SINGLE') return item;
      const next = item.quantity + delta;
      return next < 1 ? null : { ...item, quantity: next };
    }).filter(Boolean) as CartItem[]);
  };

  const removeFromCart = (id: string) => setCart((prev) => prev.filter((item) => item.id !== id));

  const handleCheckout = async () => {
    if (!cart.length) return;
    setCheckoutLoading(true);
    try {
      const res = await initiatePayment(checkoutPayload as any);
      if ((res as any).redirectUrl) {
        if (!navigateToTrustedRedirect((res as any).redirectUrl)) {
          showSnackbar('Payment redirect was blocked because it was not trusted.', 'error');
        }
      } else {
        if (res.reviewStatus === 'PENDING_ADMIN_REVIEW') {
          setCart([]);
          setIntentPreview(null);
          localStorage.removeItem(cartStorageKey);
        }
        showSnackbar(res.message || 'Payment initiated successfully.', 'success');
      }
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Checkout failed.', 'error');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const totalSetup = cart.reduce((sum, item) => sum + item.priceSetup * item.quantity, 0);
  const totalRecurring = cart.reduce((sum, item) => sum + getCartLineRecurring(item), 0);
  const channelProrationMultiplier = (channelId?: number) => {
    const period = channelId ? channelBillingPeriods[String(channelId)] : null;
    if (!period?.end) return 1;
    const end = new Date(period.end).getTime();
    const periodStart = period.start ? new Date(period.start) : new Date(period.end);
    if (!period.start) periodStart.setMonth(periodStart.getMonth() - 1);
    const start = periodStart.getTime();
    const now = Date.now();
    if (!Number.isFinite(end) || !Number.isFinite(start) || end <= now || end <= start) return 1;
    return Math.max((end - now) / Math.max(end - start, 1), 0);
  };
  const dueRecurring = addonBillingMode === 'PRORATED'
    ? cart.reduce((sum, item) => sum + (getCartLineRecurring(item) * channelProrationMultiplier(item.channelId)), 0)
    : totalRecurring;
  const totalDue = totalSetup + dueRecurring;
  const dueCurrency = intentPreview?.currency || currency;
  const checkoutDue = intentPreview?.amount ?? totalDue;
  const previewProratedRecurring = Number(intentPreview?.breakdown?.recurring ?? dueRecurring);
  const isTrialCheckout = String(user.accountStatus || '').toUpperCase() === 'TRIAL';
  const quotedLines = intentPreview?.lines || intentPreview?.breakdown?.lines || [];
  const totalCartItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const selectedChannelDueDate = selectedChannelId ? channelBillingPeriods[selectedChannelId]?.end : null;
  const groupedProducts = products.reduce<Record<ProductGroup, ProductConfig[]>>((acc, product) => {
    acc[product.group] = [...(acc[product.group] || []), product];
    return acc;
  }, { Channel: [], Packages: [], Automation: [], Delivery: [], 'Web / OTT': [] });

  return (
    <ConsolePageShell
      eyebrow="Channel Commerce"
      title="Addon Store"
      subtitle={
        effectiveBillingDate && selectedChannelDueDate
          ? `Choose a channel, add products to cart, and checkout once. First purchase: ${formatDateDDMMYY(effectiveBillingDate)} / ${selectedChannel?.name || 'Channel'} due: ${formatDateDDMMYY(selectedChannelDueDate)}`
          : 'Choose a channel, add products to cart, and checkout once.'
      }
      actions={(
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 300 } }}>
          <InputLabel>Active channel assignment</InputLabel>
          <Select value={selectedChannelId} label="Active channel assignment" onChange={(event) => setSelectedChannelId(String(event.target.value))}>
            {channels.map((channel) => (
              <MenuItem key={channel.id} value={String(channel.id)}>
                {channel.name} · {getChannelDisplayId(channel)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      stats={[
        { label: 'Assigned Channel', value: selectedChannel?.name || 'Not set', helper: selectedChannel ? getChannelDisplayId(selectedChannel) : 'select a target', tone: selectedChannel ? 'success' : 'warning' },
        { label: 'Cart Items', value: totalCartItems, helper: 'services selected' },
        { label: 'Monthly', value: money(currency, totalRecurring), helper: 'recurring total' },
        { label: 'Due Today', value: intentPreviewLoading ? 'Calculating...' : money(dueCurrency, checkoutDue), helper: isTrialCheckout ? 'activation and cart' : addonBillingMode === 'PRORATED' ? 'backend prorated quote' : 'full first month' },
      ]}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', color: TEXT, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' }, gap: 2 }}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {(['Channel', 'Packages', 'Automation', 'Delivery', 'Web / OTT'] as ProductGroup[]).map((group) => (
            <Box key={group}>
              <Box sx={{ ...cardSx, px: 1.5, py: 1.25, mb: 1.25, borderLeft: '4px solid', borderLeftColor: v('--secondary'), bgcolor: SKY_STRONG }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  {(() => {
                    const GroupIcon = groupMeta[group].icon;
                    return (
                      <Box sx={{ width: 34, height: 34, border: '1px solid', borderColor: v('--border-strong'), bgcolor: v('--bg-card'), color: v('--secondary'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <GroupIcon fontSize="small" />
                      </Box>
                    );
                  })()}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ ...labelSx, color: TEXT, fontSize: '0.78rem' }}>{groupMeta[group].title}</Typography>
                      <Chip size="small" label={`${groupedProducts[group].length} option${groupedProducts[group].length === 1 ? '' : 's'}`} sx={{ borderRadius: 0, height: 20, bgcolor: v('--bg-card'), color: MUTED, fontSize: '0.65rem', fontWeight: 800 }} />
                    </Stack>
                    <Typography sx={{ color: MUTED, fontSize: '0.74rem', mt: 0.25 }}>{groupMeta[group].subtitle}</Typography>
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                {groupedProducts[group].map((product) => (
                  <ProductCard
                    key={product.type}
                    product={product}
                    currency={currency}
                    selectedChannelId={selectedChannelId}
                    selectedTranscoderId={selectedTranscoderId}
                    onChannelChange={setSelectedChannelId}
                    onTranscoderChange={setSelectedTranscoderId}
                    selectedPlaykastrTierCode={selectedPlaykastrTierCode}
                    onPlaykastrTierChange={setSelectedPlaykastrTierCode}
                    channels={channels}
                    transcoders={channelTranscoders}
                    onAdd={() => addProductToCart(product)}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>

        <Paper elevation={0} sx={{ ...cardSx, position: { lg: 'sticky' }, top: 16, alignSelf: 'start', overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, bgcolor: SKY_STRONG, borderBottom: '1px solid', borderColor: BORDER }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <ShoppingCartIcon sx={{ color: v('--secondary') }} />
                <Typography fontWeight={950} sx={{ color: TEXT }}>Cart ({totalCartItems})</Typography>
              </Stack>
              {cart.length > 0 && (
                <Button size="small" color="error" onClick={() => setCart([])} sx={{ borderRadius: 0, textTransform: 'none', fontSize: '0.68rem', fontWeight: 800 }}>
                  Clear
                </Button>
              )}
            </Stack>
          </Box>

          <Box sx={{ maxHeight: { xs: 320, xl: 430 }, overflowY: 'auto' }}>
            {cart.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ShoppingCartIcon sx={{ fontSize: 44, color: v('--text-disabled'), mb: 1 }} />
                <Typography fontWeight={800} sx={{ color: TEXT }}>Your cart is empty</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.8rem' }}>Add services assigned to the correct channel.</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {cart.map((item) => (
                  <Box key={item.id} sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} sx={{ color: TEXT, fontSize: '0.88rem' }}>{item.label}</Typography>
                        <Typography sx={{ color: MUTED, fontSize: '0.72rem' }}>{item.description}</Typography>
                        {item.targetTranscoderLabel && <Typography sx={{ color: MUTED, fontSize: '0.7rem' }}>Upgrade: {item.targetTranscoderLabel}</Typography>}
                      </Box>
                      <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                      <Stack direction="row" alignItems="center" sx={{ border: '1px solid', borderColor: BORDER }}>
                        <IconButton size="small" disabled={item.quantityMode === 'SINGLE'} onClick={() => updateQuantity(item.id, -1)}><RemoveIcon fontSize="small" /></IconButton>
                        <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 900 }}>{item.quantity}</Typography>
                        <IconButton size="small" disabled={item.quantityMode === 'SINGLE'} onClick={() => updateQuantity(item.id, 1)}><AddIcon fontSize="small" /></IconButton>
                      </Stack>
                      <Stack alignItems="flex-end">
                        {item.priceSetup > 0 && <Typography sx={{ color: MUTED, fontSize: '0.68rem' }}>{money(currency, item.priceSetup * item.quantity)} setup</Typography>}
                        <Typography fontWeight={900} sx={{ color: v('--secondary'), fontSize: '0.82rem' }}>{money(currency, getCartLineRecurring(item))}/mo</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {cart.length > 0 && (
            <Box sx={{ p: 1.5, bgcolor: LIGHT, borderTop: '1px solid', borderColor: BORDER }}>
              <Stack spacing={1.25}>
                {isTrialCheckout && (
                  <Alert severity="info" sx={{ borderRadius: 0 }}>
                    This checkout activates your trial channel and includes its setup and first monthly charge together with every item in this cart.
                  </Alert>
                )}
                {isTrialCheckout && quotedLines.length > 0 ? (
                  <Box sx={{ border: '1px solid', borderColor: BORDER, bgcolor: v('--bg-card') }}>
                    {quotedLines.map((line, index) => (
                      <Stack key={`${line.code || line.description}-${index}`} direction="row" justifyContent="space-between" spacing={1} sx={{ px: 1, py: 0.75, borderBottom: index < quotedLines.length - 1 ? '1px solid' : 'none', borderColor: BORDER }}>
                        <Typography sx={{ color: MUTED, fontSize: '0.74rem' }}>{line.description}{line.quantity > 1 ? ` x${line.quantity}` : ''}</Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>{money(line.currency || dueCurrency, line.totalAmount)}</Typography>
                      </Stack>
                    ))}
                  </Box>
                ) : null}
                {!isTrialCheckout && totalSetup > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: MUTED }}>Setup</Typography>
                    <Typography fontWeight={900}>{money(currency, totalSetup)}</Typography>
                  </Stack>
                )}
                {!isTrialCheckout && <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: MUTED }}>Monthly</Typography>
                  <Typography fontWeight={900}>{money(currency, totalRecurring)}</Typography>
                </Stack>}
                {!isTrialCheckout && <Box sx={{ border: '1px solid', borderColor: BORDER, p: 1, bgcolor: SKY }}>
                  <Typography sx={{ color: TEXT, fontWeight: 900, fontSize: '0.78rem', mb: 0.5 }}>Billing mode</Typography>
                  <RadioGroup value={addonBillingMode} onChange={(event) => setAddonBillingMode(event.target.value as AddonBillingMode)}>
                    <FormControlLabel value="PRORATED" control={<Radio size="small" />} label={`Prorate to current billing date (${intentPreviewLoading ? 'calculating...' : money(dueCurrency, previewProratedRecurring)} now)`} />
                    <FormControlLabel value="NEW_SERVICE_DATE" control={<Radio size="small" />} label="Pay full first month; bill this service from today" />
                  </RadioGroup>
                </Box>}
                <Divider />
                <Box sx={{ border: '1px solid', borderColor: BORDER, p: 1, bgcolor: SKY_STRONG }}>
                  <Typography sx={{ color: MUTED, fontSize: '0.72rem' }}>
                    Cart items are saved on this device until you remove them or clear the cart.
                  </Typography>
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography fontWeight={950}>Total due</Typography>
                  <Typography fontWeight={950} sx={{ color: v('--secondary'), fontSize: '1.25rem' }}>
                    {intentPreviewLoading ? 'Calculating...' : money(dueCurrency, checkoutDue)}
                  </Typography>
                </Stack>
                <Alert severity="warning" sx={{ borderRadius: 0 }}>Manual payment remains pending until an administrator verifies and applies it.</Alert>
                <Button variant="contained" disabled={checkoutLoading} onClick={handleCheckout} sx={primaryButtonSx}>
                  {checkoutLoading ? 'Processing...' : isTrialCheckout ? 'Activate and checkout' : 'Checkout cart'}
                </Button>
                <Typography sx={{ color: MUTED, fontSize: '0.72rem' }}>After payment, resources are attached to the selected channel and configured under Destinations.</Typography>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </ConsolePageShell>
  );
};
